/**
 * Browser-side ODP card builder.
 *
 * Uses JSZip to unzip/rezip ODP, fast-xml-parser for lossless XML round-trip.
 * Only modifies content.xml — all other ZIP entries pass through as raw bytes.
 */

import JSZip from "jszip";
import {
  parseXml, buildXml, findElements, getAttr,
  makeElement, makeTextElement, getChildren,
} from "./xml-helpers.js";
import type { CardInput, CardResult } from "./types.js";
import { type ZoneLayout, LEFT_ZONE, RIGHT_ZONE, BLUE_FILL, ORANGE_FILL, pillLayoutStr } from "./layout.js";

const MAX_ODP_SIZE = 100 * 1024 * 1024; // 100 MB safety limit
const MASTER_PAGE = "Master2-Layout1-cust-3_自訂版面配置";
const PAGE_LAYOUT = "Master2-PPL1";

let styleCounter = 0;
function nextStyle(): string { return `b${++styleCounter}`; }
function nextId(): string { return `cb${++styleCounter}`; }
function uid(): string { return Math.random().toString(36).slice(2, 10); }

// Rounded-rect enhanced geometry (matching Python)
const ROUNDED_RECT_PATH =
  "M ?f7 0 X 0 ?f8 L 0 ?f9 Y ?f7 21600 L ?f10 21600 X 21600 ?f9 L 21600 ?f8 Y ?f10 0 Z N";
const EQUATIONS: [string, string][] = [
  ["f0", "45"], ["f1", "$0 *sin(?f0 *(pi/180))"], ["f2", "?f1 *3163/7636"],
  ["f3", "left+?f2 "], ["f4", "top+?f2 "], ["f5", "right-?f2 "], ["f6", "bottom-?f2 "],
  ["f7", "left+$0 "], ["f8", "top+$0 "], ["f9", "bottom-$0 "], ["f10", "right-$0 "],
];

function makeRoundedRectGeo(): any {
  const eqs = EQUATIONS.map(([n, f]) =>
    makeElement("draw:equation", { "draw:name": n, "draw:formula": f })
  );
  const handle = makeElement("draw:handle", {
    "draw:handle-switched": "true",
    "draw:handle-position": "$0 top",
    "draw:handle-position-x": "$0",
    "draw:handle-position-y": "top",
    "draw:handle-range-x-minimum": "0",
    "draw:handle-range-x-maximum": "10800",
  });
  return makeElement("draw:enhanced-geometry", {
    "draw:path-stretchpoint-x": "10800",
    "draw:path-stretchpoint-y": "10800",
    "draw:type": "non-primitive",
    "svg:viewBox": "0 0 21600 21600",
    "draw:enhanced-path": ROUNDED_RECT_PATH,
    "draw:text-areas": "?f3 ?f4 ?f5 ?f6",
    "draw:modifiers": "3600",
  }, [...eqs, handle]);
}

function makePlainRectGeo(): any {
  return makeElement("draw:enhanced-geometry", {
    "draw:type": "non-primitive",
    "svg:viewBox": "0 0 21600 21600",
    "draw:enhanced-path": "M 0 0 L 21600 0 21600 21600 0 21600 Z N",
  });
}

// --- Style factories ---

function makeGraphicStyle(name: string, props: Record<string, string>): any {
  const gfxProps = makeElement("style:graphic-properties", props);
  return makeElement("style:style", { "style:family": "graphic", "style:name": name }, [gfxProps]);
}

function makeTextStyle(name: string, props: Record<string, string>): any {
  const tp = makeElement("style:text-properties", props);
  return makeElement("style:style", { "style:family": "text", "style:name": name }, [tp]);
}

function makeParaStyle(name: string, props: Record<string, string>): any {
  const pp = makeElement("style:paragraph-properties", props);
  return makeElement("style:style", { "style:family": "paragraph", "style:name": name }, [pp]);
}

// --- Element builders ---

function buildPill(
  pos: [string, string], size: [string, string],
  fillColor: string, text: string, rounded: boolean,
  styles: any[],
): any {
  const gfx = nextStyle();
  const para = nextStyle();
  const txt = nextStyle();

  styles.push(makeGraphicStyle(gfx, {
    "fo:wrap-option": "wrap",
    "fo:padding-top": "0.09069in", "fo:padding-bottom": "0.09069in",
    "fo:padding-left": "0.09069in", "fo:padding-right": "0.09069in",
    "draw:textarea-vertical-align": "middle",
    "draw:textarea-horizontal-align": "center",
    "draw:fill": "solid", "draw:fill-color": fillColor,
    "draw:opacity": "100%", "draw:stroke": "none",
    "draw:auto-grow-width": "false", "draw:auto-grow-height": "false",
  }));
  styles.push(makeParaStyle(para, { "fo:text-align": "center" }));
  styles.push(makeTextStyle(txt, {
    "fo:color": "#ffffff",
    "fo:font-family": "微軟正黑體", "style:font-family-asian": "微軟正黑體",
    "fo:font-size": "0.19444in", "style:font-size-asian": "0.19444in",
    "fo:font-weight": "bold", "style:font-weight-asian": "bold",
  }));

  const span = makeTextElement("text:span", { "text:style-name": txt }, text);
  const p = makeElement("text:p", { "text:style-name": para }, [span]);
  const geo = rounded ? makeRoundedRectGeo() : makePlainRectGeo();

  return makeElement("draw:custom-shape", {
    "svg:x": pos[0], "svg:y": pos[1],
    "svg:width": size[0], "svg:height": size[1],
    "draw:id": nextId(), "draw:style-name": gfx,
    "draw:name": `pill_${uid()}`,
  }, [makeElement("svg:title", {}), makeElement("svg:desc", {}), p, geo]);
}

function buildImageFrame(
  pos: [string, string], size: [string, string],
  href: string, styles: any[],
): any {
  const gfx = nextStyle();
  styles.push(makeGraphicStyle(gfx, { "draw:fill": "none", "draw:stroke": "none" }));

  const img = makeElement("draw:image", {
    "xlink:href": href, "xlink:type": "simple",
    "xlink:show": "embed", "xlink:actuate": "onLoad",
  });
  return makeElement("draw:frame", {
    "draw:id": nextId(), "draw:style-name": gfx,
    "draw:name": `card_img_${uid()}`,
    "svg:x": pos[0], "svg:y": pos[1],
    "svg:width": size[0], "svg:height": size[1],
  }, [img, makeElement("svg:title", {}), makeElement("svg:desc", {})]);
}

function parseDescription(text: string): { text: string; bold: boolean }[] {
  const parts: { text: string; bold: boolean }[] = [];
  const re = /「([^」]+)」/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ text: text.slice(last, m.index), bold: false });
    parts.push({ text: m[0], bold: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last), bold: false });
  return parts;
}

function buildTextbox(card: CardInput, zone: ZoneLayout, styles: any[]): any {
  const hasOrange = !!card.techLabel;
  const pos = hasOrange ? zone.textboxPos : zone.textboxPosNoOrange;
  const size = hasOrange ? zone.textboxSize : zone.textboxSizeNoOrange;

  const gfx = nextStyle();
  const paraN = nextStyle();
  const labelN = nextStyle();
  const bodyN = nextStyle();
  const boldN = nextStyle();

  styles.push(makeGraphicStyle(gfx, {
    "fo:wrap-option": "wrap",
    "fo:padding-top": "0.09069in", "fo:padding-bottom": "0.09069in",
    "fo:padding-left": "0.09069in", "fo:padding-right": "0.09069in",
    "draw:textarea-vertical-align": "top", "draw:textarea-horizontal-align": "left",
    "draw:fill": "none", "draw:stroke": "none",
    "draw:auto-grow-width": "false", "draw:auto-grow-height": "true",
  }));
  styles.push(makeParaStyle(paraN, { "fo:line-height": "115%", "fo:text-align": "start" }));
  styles.push(makeTextStyle(labelN, {
    "fo:color": "#0c0c0c", "fo:font-family": "微軟正黑體",
    "style:font-family-asian": "微軟正黑體",
    "fo:font-size": "0.19444in", "style:font-size-asian": "0.19444in",
  }));
  styles.push(makeTextStyle(bodyN, {
    "fo:color": "#0c0c0c", "fo:font-family": "微軟正黑體",
    "style:font-family-asian": "微軟正黑體",
    "fo:font-size": "0.19444in", "style:font-size-asian": "0.19444in",
  }));
  styles.push(makeTextStyle(boldN, {
    "fo:color": "#000000", "fo:font-family": "Arial",
    "style:font-family-asian": "Arial",
    "fo:font-size": "0.19444in", "style:font-size-asian": "0.19444in",
    "fo:font-weight": "bold", "style:font-weight-asian": "bold",
  }));

  function bullet(label: string, spans: { text: string; bold: boolean }[]): any {
    const children = [
      makeTextElement("text:span", { "text:style-name": labelN }, ` - ${label}`),
      ...spans.map(s =>
        makeTextElement("text:span", { "text:style-name": s.bold ? boldN : bodyN }, s.text)
      ),
    ];
    return makeElement("text:p", { "text:style-name": paraN }, children);
  }

  const textboxChildren: any[] = [];
  textboxChildren.push(bullet("規格：", [{ text: card.spec, bold: false }]));
  if (card.topic) {
    textboxChildren.push(bullet("題目：", [{ text: card.topic, bold: false }]));
  }
  textboxChildren.push(bullet("說明：", parseDescription(card.description)));

  const tb = makeElement("draw:text-box", {}, textboxChildren);
  return makeElement("draw:frame", {
    "draw:id": nextId(), "draw:style-name": gfx,
    "draw:name": `card_text_${uid()}`,
    "svg:x": pos[0], "svg:y": pos[1],
    "svg:width": size[0], "svg:height": size[1],
  }, [tb, makeElement("svg:title", {}), makeElement("svg:desc", {})]);
}

function buildTitleFrame(styles: any[]): any {
  const gfx = nextStyle();
  const para = nextStyle();
  const txt = nextStyle();
  styles.push(makeGraphicStyle(gfx, {
    "fo:wrap-option": "wrap", "draw:fill": "none", "draw:stroke": "none",
    "draw:textarea-vertical-align": "middle", "draw:textarea-horizontal-align": "left",
    "draw:auto-grow-width": "false", "draw:auto-grow-height": "false",
  }));
  styles.push(makeParaStyle(para, { "fo:text-align": "start" }));
  styles.push(makeTextStyle(txt, {
    "fo:color": "#3f3f3f", "fo:font-family": "Calibri",
    "style:font-family-asian": "Calibri",
    "fo:font-size": "0.33333in", "style:font-size-asian": "0.33333in",
    "fo:font-weight": "bold", "style:font-weight-asian": "bold",
  }));
  const span = makeTextElement("text:span", { "text:style-name": txt }, "貼文切角建議");
  const p = makeElement("text:p", { "text:style-name": para }, [span]);
  const tb = makeElement("draw:text-box", {}, [p]);
  return makeElement("draw:frame", {
    "draw:id": nextId(), "draw:style-name": gfx,
    "draw:name": `title_${uid()}`,
    "svg:x": "0.7017in", "svg:y": "0.43015in",
    "svg:width": "11.92979in", "svg:height": "0.625in",
  }, [tb, makeElement("svg:title", {}), makeElement("svg:desc", {})]);
}

function buildPageNumFrame(styles: any[]): any {
  const gfx = nextStyle();
  const para = nextStyle();
  const txt = nextStyle();
  styles.push(makeGraphicStyle(gfx, {
    "draw:fill": "none", "draw:stroke": "none",
    "draw:auto-grow-width": "false", "draw:auto-grow-height": "false",
  }));
  styles.push(makeParaStyle(para, { "fo:text-align": "end" }));
  styles.push(makeTextStyle(txt, {
    "fo:color": "#888888", "fo:font-family": "Calibri",
    "style:font-family-asian": "Calibri",
    "fo:font-size": "0.13889in", "style:font-size-asian": "0.13889in",
  }));
  const pn = makeElement("text:page-number", { "style:num-format": "1", "text:fixed": "false" });
  const span = makeElement("text:span", { "text:style-name": txt }, [pn]);
  const p = makeElement("text:p", { "text:style-name": para }, [span]);
  const tb = makeElement("draw:text-box", {}, [p]);
  return makeElement("draw:frame", {
    "draw:id": nextId(), "presentation:style-name": gfx,
    "draw:name": `pagenum_${uid()}`,
    "svg:x": "12.60052in", "svg:y": "6.98548in",
    "svg:width": "0.59055in", "svg:height": "0.34908in",
    "presentation:class": "page-number",
    "presentation:placeholder": "false",
  }, [tb, makeElement("svg:title", {}), makeElement("svg:desc", {})]);
}

// --- Page scanning ---

function findCardPages(pages: any[]): { index: number; hasLeft: boolean; hasRight: boolean }[] {
  const boundary = (() => {
    const leftMax = Math.max(
      parseFloat(LEFT_ZONE.bluePillPos[0]), parseFloat(LEFT_ZONE.orangePillPos[0]),
    ) + Math.max(
      parseFloat(LEFT_ZONE.bluePillSize[0]), parseFloat(LEFT_ZONE.orangePillSize[0]),
    );
    const rightMin = Math.min(
      parseFloat(RIGHT_ZONE.bluePillPos[0]), parseFloat(RIGHT_ZONE.orangePillPos[0]),
    );
    return (leftMax + rightMin) / 2;
  })();

  const results: { index: number; hasLeft: boolean; hasRight: boolean }[] = [];

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];
    const children = getChildren(page);
    let isCardPage = false;
    let hasLeft = false;
    let hasRight = false;

    for (const child of children) {
      // Check for title text "貼文切角建議"
      const spans = findElements([child], "text:span");
      for (const s of spans) {
        const sc = getChildren(s);
        for (const c of sc) {
          if (c["#text"] && String(c["#text"]).includes("貼文切角建議")) {
            isCardPage = true;
          }
        }
      }
      // Check for custom shapes (pills) to detect left/right cards
      if ("draw:custom-shape" in child) {
        const x = getAttr(child, "svg:x");
        if (x) {
          const xVal = parseFloat(x);
          if (xVal < boundary) hasLeft = true;
          else hasRight = true;
        }
      }
    }

    if (isCardPage) {
      results.push({ index: i, hasLeft, hasRight });
    }
  }
  return results;
}

// --- Blank ODP generator ---

function createBlankOdpZip(): JSZip {
  const zip = new JSZip();
  zip.file("mimetype", "application/vnd.oasis.opendocument.presentation", { compression: "STORE" });
  zip.file("content.xml", [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<office:document-content xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0"',
    ' xmlns:style="urn:oasis:names:tc:opendocument:xmlns:style:1.0"',
    ' xmlns:text="urn:oasis:names:tc:opendocument:xmlns:text:1.0"',
    ' xmlns:table="urn:oasis:names:tc:opendocument:xmlns:table:1.0"',
    ' xmlns:draw="urn:oasis:names:tc:opendocument:xmlns:drawing:1.0"',
    ' xmlns:fo="urn:oasis:names:tc:opendocument:xmlns:xsl-fo-compatible:1.0"',
    ' xmlns:xlink="http://www.w3.org/1999/xlink"',
    ' xmlns:svg="urn:oasis:names:tc:opendocument:xmlns:svg-compatible:1.0"',
    ' xmlns:presentation="urn:oasis:names:tc:opendocument:xmlns:presentation:1.0"',
    ' office:version="1.4">',
    '<office:automatic-styles/>',
    '<office:body><office:presentation/></office:body>',
    '</office:document-content>',
  ].join("\n"));
  zip.file("styles.xml", [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<office:document-styles xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0"',
    ' xmlns:style="urn:oasis:names:tc:opendocument:xmlns:style:1.0"',
    ' xmlns:draw="urn:oasis:names:tc:opendocument:xmlns:drawing:1.0"',
    ' xmlns:fo="urn:oasis:names:tc:opendocument:xmlns:xsl-fo-compatible:1.0"',
    ' xmlns:presentation="urn:oasis:names:tc:opendocument:xmlns:presentation:1.0"',
    ' office:version="1.4">',
    '<office:automatic-styles>',
    '  <style:page-layout style:name="pageLayout1">',
    '    <style:page-layout-properties fo:page-width="13.33333in" fo:page-height="7.5in"/>',
    '  </style:page-layout>',
    '</office:automatic-styles>',
    '<office:master-styles/>',
    '</office:document-styles>',
  ].join("\n"));
  zip.file("META-INF/manifest.xml", [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<manifest:manifest xmlns:manifest="urn:oasis:names:tc:opendocument:xmlns:manifest:1.0" manifest:version="1.3">',
    '  <manifest:file-entry manifest:full-path="/" manifest:version="1.4" manifest:media-type="application/vnd.oasis.opendocument.presentation"/>',
    '  <manifest:file-entry manifest:full-path="content.xml" manifest:media-type="text/xml"/>',
    '  <manifest:file-entry manifest:full-path="styles.xml" manifest:media-type="text/xml"/>',
    '</manifest:manifest>',
  ].join("\n"));
  return zip;
}

// --- Public API ---

export async function addCardToOdp(
  odpBlob: Blob | null,
  card: CardInput,
): Promise<{ blob: Blob; result: CardResult }> {
  if (odpBlob && odpBlob.size > MAX_ODP_SIZE) {
    throw new Error(`ODP 檔案過大 (${(odpBlob.size / 1024 / 1024).toFixed(1)} MB)，上限 100 MB`);
  }

  const zip = odpBlob ? await JSZip.loadAsync(odpBlob) : createBlankOdpZip();
  const contentFile = zip.file("content.xml");
  if (!contentFile) throw new Error("content.xml 不存在");
  const contentXmlRaw = await contentFile.async("string");
  const parsed = parseXml(contentXmlRaw);

  // Find office:body > office:presentation
  const bodies = findElements(parsed, "office:body");
  if (bodies.length === 0) throw new Error("content.xml 中找不到 office:body");
  const presentations = findElements(getChildren(bodies[0]), "office:presentation");
  if (presentations.length === 0) throw new Error("content.xml 中找不到 office:presentation");
  const presNode = presentations[0];
  const presChildren = getChildren(presNode);

  // Find auto-styles
  const autoStylesNodes = findElements(parsed, "office:automatic-styles");
  const autoStyles = autoStylesNodes.length > 0 ? getChildren(autoStylesNodes[0]) : [];

  // Reset counter to avoid collisions — scan existing style names
  styleCounter = 0;
  for (const s of autoStyles) {
    const name = getAttr(s, "style:name") || "";
    const m = name.match(/^[ab](\d+)$/);
    if (m && m[1]) styleCounter = Math.max(styleCounter, parseInt(m[1], 10));
  }

  // Find all draw:page nodes in presentation
  const pageNodes: any[] = [];
  const nonPageNodes: any[] = [];
  for (const child of presChildren) {
    if ("draw:page" in child) {
      pageNodes.push(child);
    } else {
      nonPageNodes.push(child);
    }
  }

  // Determine placement
  const cardPages = findCardPages(pageNodes);
  let targetPageIndex: number;
  let zone: "left" | "right";
  let wasNewPage = false;

  const openPageLeft = cardPages.find(p => !p.hasLeft);
  if (openPageLeft) {
    targetPageIndex = openPageLeft.index;
    zone = "left";
  } else {
    const halfPage = cardPages.find(p => p.hasLeft && !p.hasRight);
    if (halfPage) {
      targetPageIndex = halfPage.index;
      zone = "right";
    } else {
      wasNewPage = true;
      targetPageIndex = pageNodes.length;
      zone = "left";
    }
  }

  const zoneLayout = zone === "left" ? LEFT_ZONE : RIGHT_ZONE;
  const newStyles: any[] = [];
  const newElements: any[] = [];

  if (wasNewPage) {
    const pageGfx = nextStyle();
    newStyles.push(makeElement("style:style", {
      "style:family": "drawing-page", "style:name": pageGfx,
    }, [makeElement("style:drawing-page-properties", {
      "draw:fill": "solid", "draw:fill-color": "#ffffff",
      "draw:opacity": "100%", "presentation:visibility": "visible",
      "draw:background-size": "border",
      "presentation:background-objects-visible": "true",
      "presentation:background-visible": "true",
    })]));

    const titleFrame = buildTitleFrame(newStyles);
    const pageNumFrame = buildPageNumFrame(newStyles);

    const newPage = makeElement("draw:page", {
      "draw:name": `CardPage_${uid()}`,
      "draw:style-name": pageGfx,
      "draw:master-page-name": MASTER_PAGE,
      "presentation:presentation-page-layout-name": PAGE_LAYOUT,
      "draw:id": `Slide-${uid()}`,
    }, [titleFrame, pageNumFrame]);

    pageNodes.push(newPage);
  }

  // Embed images + read natural dimensions
  const embeddedImages: { href: string; natW: number; natH: number }[] = [];
  for (const imgFile of card.images) {
    const ext = imgFile.name.split(".").pop()?.toLowerCase() || "png";
    const internalPath = `Pictures/card_${uid()}.${ext}`;
    const buf = await imgFile.arrayBuffer();
    zip.file(internalPath, buf);
    const bmp = await createImageBitmap(new Blob([buf]));
    embeddedImages.push({ href: internalPath, natW: bmp.width, natH: bmp.height });
    bmp.close();
  }

  // Build card image frames with aspect-ratio-preserving sizes
  const areaW = parseFloat(zoneLayout.imageSize[0]);
  const areaH = parseFloat(zoneLayout.imageSize[1]);
  const baseX = parseFloat(zoneLayout.imagePos[0]);
  const baseY = parseFloat(zoneLayout.imagePos[1]);

  if (embeddedImages.length === 1) {
    const img = embeddedImages[0]!;
    const aspect = img.natW / img.natH;
    const areaAspect = areaW / areaH;
    let w: number, h: number;
    if (aspect > areaAspect) { w = areaW; h = areaW / aspect; }
    else { h = areaH; w = areaH * aspect; }
    const ox = baseX + (areaW - w) / 2;
    const oy = baseY + (areaH - h) / 2;
    newElements.push(buildImageFrame(
      [`${ox.toFixed(5)}in`, `${oy.toFixed(5)}in`],
      [`${w.toFixed(5)}in`, `${h.toFixed(5)}in`],
      img.href, newStyles,
    ));
  } else {
    const n = embeddedImages.length;
    const cols = n <= 2 ? n : n <= 4 ? 2 : 3;
    const rows = Math.ceil(n / cols);
    const gap = 0.08;
    const cellW = (areaW - gap * (cols - 1)) / cols;
    const cellH = (areaH - gap * (rows - 1)) / rows;

    for (let i = 0; i < n; i++) {
      const img = embeddedImages[i]!;
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cellX = baseX + col * (cellW + gap);
      const cellY = baseY + row * (cellH + gap);

      const aspect = img.natW / img.natH;
      const cellAspect = cellW / cellH;
      let w: number, h: number;
      if (aspect > cellAspect) { w = cellW; h = cellW / aspect; }
      else { h = cellH; w = cellH * aspect; }
      const ox = cellX + (cellW - w) / 2;
      const oy = cellY + (cellH - h) / 2;

      newElements.push(buildImageFrame(
        [`${ox.toFixed(5)}in`, `${oy.toFixed(5)}in`],
        [`${w.toFixed(5)}in`, `${h.toFixed(5)}in`],
        img.href, newStyles,
      ));
    }
  }

  // Blue pill (shorter when orange pill present)
  const pl = pillLayoutStr(zoneLayout, !!card.techLabel);
  newElements.push(buildPill(
    pl.bluePos, pl.blueSize,
    BLUE_FILL, card.title, true, newStyles,
  ));

  // Orange pill (optional)
  if (card.techLabel) {
    newElements.push(buildPill(
      pl.orangePos, pl.orangeSize,
      ORANGE_FILL, card.techLabel, false, newStyles,
    ));
  }

  // Text box
  newElements.push(buildTextbox(card, zoneLayout, newStyles));

  // Inject styles into auto-styles
  for (const s of newStyles) {
    autoStyles.push(s);
  }

  // Inject elements into target page
  const targetPage = pageNodes[targetPageIndex];
  const targetChildren = getChildren(targetPage);
  for (const el of newElements) {
    targetChildren.push(el);
  }

  // Reassemble presentation children: pages first, then non-page nodes
  const presTag = Object.keys(presNode).find(k => k !== ":@" && !k.startsWith("#"))!;
  presNode[presTag] = [...pageNodes, ...nonPageNodes];

  // Build XML and write back
  const newContentXml = buildXml(parsed);
  zip.file("content.xml", newContentXml);

  const resultBlob = await zip.generateAsync({ type: "blob", mimeType: "application/vnd.oasis.opendocument.presentation" });

  return {
    blob: resultBlob,
    result: {
      pageIndex: targetPageIndex,
      zone,
      wasNewPage,
    },
  };
}
